import React, { useEffect, useMemo, useState } from "react";
import { injectIntl, intlShape } from "react-intl";
import { v4 as uuid } from "uuid";

import {
    DialogBox,
    DialogBoxFooterType,
    NoLink,
    Section,
    SelectInput,
    TextInput,
    ZeroToInfiniteGrid,
} from "~/core";
import { ITargetLevelName } from "./model";
import { messages } from "../../i18n-messages";
import {
    actions as picklistActions,
    picklistNames,
    selectors as picklistSelectors,
} from "~/core/picklist";
import { createAddLinkLabelText } from "~/i18n-messages";

import "./target-level-names.css";
import { useDispatch, useSelector } from "react-redux";

interface IProps {
    intl: intlShape;
    list: ITargetLevelName[];
    orgLevelGuid: string | null; // if null, then this is system level
    updateList: (values: ITargetLevelName[]) => void;
}

interface IDisplayRecord extends ITargetLevelName {
    levelColumn: string;
}

function TargetLevelNames_(props: IProps) {
    const { formatMessage } = props.intl;
    const { list } = props;

    const targetLevelNameLabel = formatMessage(
        !props.orgLevelGuid ? messages.targetLevelNames : messages.targetLevelNamesOverride
    );

    const [showDialog, setShowDialog] = useState(false);
    const [editRow, setEditRow] = useState<number | null>(null);

    const [sampleTypeGuid, setSampleTypeGuid] = useState<string>(null);
    const [level1, setLevel1] = useState<string>(null);
    const [level2, setLevel2] = useState<string>(null);
    const [level3, setLevel3] = useState<string>(null);
    const [level4, setLevel4] = useState<string>(null);
    const [level5, setLevel5] = useState<string>(null);
    const [level6, setLevel6] = useState<string>(null);

    const dispatch = useDispatch();
    const sampleTypeList = useSelector((state) => {
        return picklistSelectors
            .getPicklistOptionsFromCode(
                state,
                picklistNames.getPickListCode(picklistNames.PICKLIST_SAMPLE_TYPE)
            )
            .filter((item) => ["Tissue", "Soil", "Nematode"].includes(item.label));
    });

    const displayRecords = useMemo<IDisplayRecord[]>(
        () =>
            list?.map((r) => ({
                ...r,
                levelColumn: [
                    r.level1Name,
                    r.level2Name,
                    r.level3Name,
                    r.level4Name,
                    r.level5Name,
                    r.level6Name,
                ]
                    .filter((x) => x)
                    .join(", "),
            })) ?? [],
        [list]
    );

    const displaySampleTypes = useMemo(
        () =>
            (!list
                ? sampleTypeList
                : sampleTypeList?.filter(
                      (type) =>
                          list.findIndex(
                              (value, index) =>
                                  index !== editRow && value.sampleTypeGuid === type.value
                          ) == -1
                  )) ?? [],
        [editRow, sampleTypeList, list]
    );

    useEffect(() => {
        if ((sampleTypeList?.length ?? 0) === 0) {
            dispatch(
                picklistActions.fetchPicklistData({
                    [picklistNames.PICKLIST_SAMPLE_TYPE]: picklistNames.getPickListCode(
                        picklistNames.PICKLIST_SAMPLE_TYPE
                    ),
                })
            );
        }
    }, []);

    const invalidSave = useMemo(
        () =>
            [sampleTypeGuid, level1, level2, level3, level4, level5, level6].findIndex(
                (x) => !x
            ) !== -1,
        [sampleTypeGuid, level1, level2, level3, level4, level5, level6]
    );

    const closeDialog = () => {
        setShowDialog(false);
        setEditRow(null);
        setSampleTypeGuid(null);
        setLevel1(null);
        setLevel2(null);
        setLevel3(null);
        setLevel4(null);
        setLevel5(null);
        setLevel6(null);
    };

    const deleteHandler = !props.orgLevelGuid
        ? undefined
        : (_, index) => {
              const newList = list.filter((x, i) => i !== index);
              props.updateList(newList);
          };

    return (
        <Section
            required={!props.orgLevelGuid}
            headerText={targetLevelNameLabel}
            className="grid-section target-levels"
        >
            <div className="form-section-child-stretch mini-grid">
                {displayRecords.length > 0 && (
                    <ZeroToInfiniteGrid
                        className={"target-level-grid"}
                        records={displayRecords}
                        onEdit={(record: ITargetLevelName, index) => {
                            setEditRow(index);
                            setSampleTypeGuid(record.sampleTypeGuid);
                            setLevel1(record.level1Name);
                            setLevel2(record.level2Name);
                            setLevel3(record.level3Name);
                            setLevel4(record.level4Name);
                            setLevel5(record.level5Name);
                            setLevel6(record.level6Name);
                            setShowDialog(true);
                        }}
                        onDelete={deleteHandler}
                        columns={{
                            sampleTypeName: {
                                title: formatMessage(messages.sampleType),
                            },
                            levelColumn: {
                                title: targetLevelNameLabel,
                                className: "combined-names",
                            },
                        }}
                    />
                )}
                {!!props.orgLevelGuid && (
                    <div className="add-link-container">
                        <NoLink
                            className="add-link"
                            disabled={displaySampleTypes.length === 0}
                            label={createAddLinkLabelText(formatMessage, messages.override)}
                            onClick={() => {
                                setShowDialog(true);
                                if (displaySampleTypes.length === 1) {
                                    setSampleTypeGuid(displaySampleTypes[0].value as string);
                                }
                            }}
                        />
                    </div>
                )}
            </div>
            {showDialog && (
                <DialogBox
                    isOpen={true}
                    unrestricted={true}
                    className="add-edit-dialog"
                    actionDisabled={invalidSave}
                    footerType={DialogBoxFooterType.ACTION_CANCEL}
                    action={formatMessage(messages.save)}
                    title={`${formatMessage(
                        editRow !== null ? messages.edit : messages.add
                    )} ${targetLevelNameLabel}`}
                    onAction={() => {
                        const row: ITargetLevelName = {
                            targetLevelNameGuid:
                                editRow !== null ? list[editRow].targetLevelNameGuid : uuid(),
                            sampleTypeGuid: sampleTypeGuid,
                            orgLevelGuid: props.orgLevelGuid,
                            level1Name: level1,
                            level2Name: level2,
                            level3Name: level3,
                            level4Name: level4,
                            level5Name: level5,
                            level6Name: level6,
                            sampleTypeName:
                                editRow !== null
                                    ? list[editRow].sampleTypeName
                                    : sampleTypeList.find((x) => x.value === sampleTypeGuid).label,
                        };

                        const newList = [...list];

                        if (editRow !== null) {
                            newList[editRow] = row;
                        } else {
                            newList.push(row);
                        }

                        props.updateList(newList);
                        closeDialog();
                    }}
                    onClose={closeDialog}
                >
                    <SelectInput<string>
                        key="sample-type"
                        required
                        disabled={editRow !== null}
                        placeholderText={formatMessage(messages.sampleType)}
                        options={displaySampleTypes}
                        value={sampleTypeGuid}
                        onChange={(value: string) => {
                            setSampleTypeGuid(value);
                        }}
                    />
                    <div>&nbsp;</div>
                    <TextInput
                        placeholderText={formatMessage(messages.levelName1)}
                        required
                        maxLength={30}
                        value={level1}
                        onChange={(value) => setLevel1(value)}
                    />
                    <TextInput
                        placeholderText={formatMessage(messages.levelName2)}
                        required
                        maxLength={30}
                        value={level2}
                        onChange={(value) => setLevel2(value)}
                    />
                    <TextInput
                        placeholderText={formatMessage(messages.levelName3)}
                        required
                        maxLength={30}
                        value={level3}
                        onChange={(value) => setLevel3(value)}
                    />
                    <TextInput
                        placeholderText={formatMessage(messages.levelName4)}
                        required
                        maxLength={30}
                        value={level4}
                        onChange={(value) => setLevel4(value)}
                    />
                    <TextInput
                        placeholderText={formatMessage(messages.levelName5)}
                        required
                        maxLength={30}
                        value={level5}
                        onChange={(value) => setLevel5(value)}
                    />
                    <TextInput
                        placeholderText={formatMessage(messages.levelName6)}
                        required
                        maxLength={30}
                        value={level6}
                        onChange={(value) => setLevel6(value)}
                    />
                </DialogBox>
            )}
        </Section>
    );
}

export default injectIntl(TargetLevelNames_);
